import { OverflowTooltip } from '@/components/overflow-tooltip'
import { Table as BaseTable, TableMenu } from '@/components_deprecated'
import type { Column } from '@/components_deprecated/Table'
import { Icon } from '@ur-solutions/react-components'
import { parseISO } from 'date-fns'
import React, { useMemo } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { format } from '@/utils/date-fns'
import type {
  TimesheetsSalaryWorkTime,
  UserTimeEntryNode,
} from '../types.graphql'
import { OldDateCell } from './table-cells'
import { useTimesheetHandlers } from './handler.hooks'
import { useFormatTimeEntryLastAction } from './TimesheetTableRow/helpers/latestAction'

const Table = styled(BaseTable)`
  position: relative;

  ${props => props.theme.media.largePad}
  ${props => props.theme.media.mobile} {
    overflow: auto;
  }
`

const Row = styled.tr`
  td {
    box-sizing: content-box;
    height: 30px;
  }
`

const LastActionCell = styled.td<{ clickable?: boolean }>`
  ${props =>
    props.clickable &&
    css`
      cursor: pointer;
    `}

  overflow: hidden;
`

const LastActionTextStyled = styled.span`
  display: inline-block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const StyledIcon = styled(Icon)`
  cursor: pointer;
`

const LastActionText: React.FC<{ children: string }> = ({ children }) => {
  if (!children) return <LastActionTextStyled />

  return (
    <OverflowTooltip text={children}>
      <LastActionTextStyled>{children}</LastActionTextStyled>
    </OverflowTooltip>
  )
}

interface SalaryTimeEntriesTableProps {
  columns: Column[]
  entries: UserTimeEntryNode[]
  projectActive?: boolean
  salaryWorkTime: TimesheetsSalaryWorkTime
}

const hasVisibleComment = (comment?: string | null) => {
  if (!comment) return false

  return !['Ingen kommentar', 'No comment'].includes(comment.trim())
}

export const SalaryTimeEntriesTable: React.FC<SalaryTimeEntriesTableProps> = ({
  columns,
  entries,
  projectActive = false,
  salaryWorkTime,
}) => {
  const theme = useTheme()
  const formatLastAction = useFormatTimeEntryLastAction()
  const { timeEntryHandler, timesheetHandler } =
    useTimesheetHandlers(salaryWorkTime)
  const showSalaryColumns = columns.some(column => column.name === 'salaryCode')

  const sortedEntries = useMemo(
    () =>
      [...entries].sort(
        (left, right) =>
          new Date(left.datetimeStart).getTime() -
          new Date(right.datetimeStart).getTime()
      ),
    [entries]
  )

  return (
    <Table headerMargin rowHoverBackground="gray10">
      <thead>
        <tr>
          {columns.map(column => (
            <th key={column.name}>{column.displayName || column.name}</th>
          ))}
          <th key="menu"></th>
        </tr>
      </thead>

      <tbody>
        {sortedEntries.map(entry => {
          const latestAction = formatLastAction(entry.latestActionLog)
          const menuItems = timeEntryHandler.createTimesheetTimeEntryMenuItems(
            entry,
            sortedEntries.filter(otherEntry => otherEntry.id !== entry.id)
          )

          return (
            <Row key={entry.id}>
              <td>{format(parseISO(entry.datetimeStart), 'dd. MMM')}</td>

              <OldDateCell
                date={parseISO(entry.datetimeStart)}
                side="start"
                type="timeentry"
              />

              {entry.datetimeEnd ? (
                <OldDateCell
                  date={parseISO(entry.datetimeEnd)}
                  side="end"
                  type="timeentry"
                />
              ) : (
                <td></td>
              )}

              <td>{entry.totalOrdinary ?? entry.totalWork ?? '-'}</td>

              {showSalaryColumns && (
                <>
                  <td>{entry.salaryCodeOrdinary?.name ?? '-'}</td>
                  <td>{entry.department?.name ?? '-'}</td>
                  {projectActive && <td>{entry.facility?.name ?? '-'}</td>}
                </>
              )}

              <LastActionCell
                clickable={!!entry.latestActionLog}
                onClick={e => {
                  e.stopPropagation()
                  if (entry.latestActionLog) {
                    void timeEntryHandler.viewLogs(entry)
                  }
                }}
              >
                <LastActionText>{latestAction}</LastActionText>
              </LastActionCell>

              <td width="130px">
                {hasVisibleComment(entry.comment) && (
                  <IconContainer>
                    <StyledIcon
                      color={theme.colors.gray1}
                      hoverColor={theme.colors.secondary}
                      icon="comment-alt-lines"
                      size="16px"
                      onClick={() =>
                        timesheetHandler.viewComment(entry.comment || '')
                      }
                    />
                  </IconContainer>
                )}
              </td>

              {menuItems.length > 0 ? (
                <TableMenu items={menuItems} />
              ) : (
                <td></td>
              )}
            </Row>
          )
        })}
      </tbody>
    </Table>
  )
}
